import _ from "lodash";
import React, { useEffect, useState } from "react";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import AssessmentToolV2 from "../AssessmentToolV2";

// Module and GraphqlHelpers
import {
  updateAssessmentInRedux,
  removeAssessmentTool,
  updateAssessmentToolSetting,
} from "Assessments/modules/AssessmentModule";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  updateScoreToolData,
  writeScoreToolDataInCache,
  writeAssessmentScoreToolDataInCache,
} from "modules/AssessmentTool/AssessmentToolModule";
import { setMaxScoreValue } from "Teacher/modules/TeacherModule";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";
import {
  CUSTOM_WEIGHTS,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  DP_ASSESSMENT_RUBRIC,
  SCORE,
  COMMENT,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import {
  getGradeCalculationMethodSetting,
  getGradeCalculationMethodCategory,
} from "routes/routes/Administrator/routes/DPAcademicSetup/routes/AssessmentSetup/modules/utils";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getAssessmentGradingConfigurationsQuery } from "GradingSetupV2/modules/GradingSetupV2Queries";

// Services
import { getPrintFile } from "modules/PrintThunks";
import ACLStore from "lib/aclStore";

// Design System
import {
  Dropdown,
  DropdownMenu,
  IconButton,
  MediaCard,
  TextInput,
  ToggleSwitch,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  DotsVerticalOutlined,
  PencilOutlined,
  PrintOutlined,
  UserProfileOutlined,
  StudentOutlined,
  WarningTriangleOutlined,
  WeightOutlined,
  InformationFilled,
} from "@toddle-edu/ds-icons";

// External libs.
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";

// Utils
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import { isAssessmentToolPreviewAvailable } from "Utils";
import {
  getAssessmentToolText,
  getAllowTeacherAndStudentEval,
  getAssessmentToolTypeForMediaCard,
  getEvaluationTexts,
  getAutoEvaluateIBGradeToolTypeName,
  getIsCustomWeightsV2,
} from "ClassRoom/modules/utils";
import { getTotalScoreMemoized } from "AppComponents/CriteriaRubricAssessment/Utils";
import { DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX } from "Constants";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";

// scss
import classes from "./CustomMediaCard.scss";

// Hooks
import { useFetchQuery } from "Hooks";

const styles = {
  scoreInput: {
    width: "90px",
    maxWidth: "100%",
  },
  overlayInnerStyle: {
    maxWidth: "188px",
    padding: "8px",
    textAlign: "center",
  },
  tooltipStyle: {
    maxWidth: "248px",
  },
  weightInfoIconStyle: {
    marginLeft: "4px",
    marginRight: "20px",
  },
};

const TEACHER_EVALUATION_DISABLED_TOOLS = [
  COMMENT,
  SCORE,
  DP_ASSESSMENT_RUBRIC,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
];

const NON_CLICKABLE_TOOLS = [COMMENT, SCORE, ASSESSMENT_GRADE_BOUNDARY];

const getAssessmentToolTypeForInstructionsMediaCard = params => {
  const { t, assessmentToolType, assessmentTool } = params;
  let subtext = "";
  switch (assessmentToolType) {
    case SCORE:
      {
        subtext = t("common:score");
      }
      break;
    case DP_ASSESSMENT_RUBRIC: {
      subtext = _.get(assessmentTool, "label", "");
    }
  }

  return getAssessmentToolTypeForMediaCard({
    t,
    assessmentToolType,
    subtext,
  });
};

const getAssessmentToolTypeForMediaCardWrapper = params => {
  const {
    t,
    assessmentToolType,
    maxScore,
    assessmentTool,
    isInstructionsRevamp = false,
    mode,
  } = params;

  if (isInstructionsRevamp && mode === "view") {
    return getAssessmentToolTypeForInstructionsMediaCard({
      t,
      assessmentTool,
      assessmentToolType,
      maxScore,
    });
  }

  let subtext = "";

  switch (assessmentToolType) {
    case SCORE:
      subtext = t("common:score_based_assessment_with_marks", {
        label: maxScore,
      });
      break;
    case DP_ASSESSMENT_RUBRIC: {
      const totalMarks = getEditModeSubtext(params);
      subtext = (
        <div className={classes.scoreContainer}>
          <span>{t("classRoom:dp_criteria")}</span>
          {!_.isNil(totalMarks) ? (
            <>
              <span className={classes.dot} />
              <span>{totalMarks}</span>
            </>
          ) : null}
        </div>
      );
      break;
    }
  }

  return getAssessmentToolTypeForMediaCard({
    t,
    assessmentToolType,
    subtext,
  });
};

const getEditModeSubtext = params => {
  const { t, assessmentTool, assessmentToolType } = params;

  switch (assessmentToolType) {
    case ASSESSMENT_GRADE_BOUNDARY:
      return t("classRoom:grade_manually");
    case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      return (
        <React.Fragment>
          {t("classRoom:grade_automatically")}
          {" - "}
          {getAutoEvaluateIBGradeToolTypeName({ t, assessmentToolType })}
        </React.Fragment>
      );
    case DP_ASSESSMENT_RUBRIC: {
      const criteria = _.get(assessmentTool, "criteria", []);

      if (!_.isEmpty(criteria)) {
        const totalScore = getTotalScoreMemoized({
          criterionIds: [],
          criteria,
        });

        return t("classRoom:max_marks_with_score", { score: totalScore });
      }

      return null;
    }
    default: {
      return null;
    }
  }
};

const getOptions = ({ t, type, assessmentTools }) => {
  const assessmentToolTypeLocale = t(
    _.find(ASSESSMENT_TOOLS, { type })?.locale
  );

  const toolsWithOnlyRemoveOption = [ASSESSMENT_GRADE_BOUNDARY];

  if (type === "DP_ASSESSMENT_RUBRIC") {
    const dpRubric = _.find(assessmentTools, ({ node }) => {
      return _.get(node, "assessmentToolType") === type;
    });

    const isDPRubricGlobal = _.get(
      dpRubric,
      "node.assessmentToolItem.isGlobal",
      true
    );

    if (isDPRubricGlobal) {
      toolsWithOnlyRemoveOption.push("DP_ASSESSMENT_RUBRIC");
    }
  }

  const showOnlyRemoveButton = _.includes(toolsWithOnlyRemoveOption, type);
  const showEditToolOption = !_.includes(["SCORE", "COMMENT"], type);

  if (showOnlyRemoveButton) {
    return [
      {
        key: "remove_tool",
        label: t("common:remove"),
        icon: <DeleteOutlined variant={"critical"} />,
        isDestructive: true,
      },
    ];
  } else {
    const options = [];

    if (showEditToolOption) {
      options.push({
        key: "edit_tool",
        label: t("common:edit_with_label", {
          label: assessmentToolTypeLocale,
        }),
        icon: <PencilOutlined />,
      });
    }

    options.push({
      key: "remove_tool",
      label: t("common:remove"),
      icon: <DeleteOutlined />,
      isDestructive: true,
    });

    return options;
  }
};

const onCardActionClick = params => {
  const {
    assessmentToolId,
    type,
    action,
    toggleAssessmentToolModal,
    updateAssessmentToolData,
    updateAssessmentInRedux,
    updateInputField,
    portalType,
  } = params;

  switch (action) {
    case "edit_tool":
      {
        updateAssessmentToolData({
          assessmentToolModalMode: "edit_tool",
        });

        updateAssessmentInRedux({
          assessmentToolId,
          assessmentToolType: type,
        });

        toggleAssessmentToolModal();
      }
      break;
    case "remove_tool":
      {
        const params = {
          measureAssessing: type,
          portalType,
        };
        updateInputField(params, { id: assessmentToolId, type });
      }
      break;
  }
};

const getMediaCardViewOptions = params => {
  const {
    type,
    showToolPrintOption = false,
    mode,
    getPrintFile,
    assessmentToolId,
    resourceId,
    parentId,
    assessmentId,
  } = params || {};

  const canPrint = _.get(_.find(ASSESSMENT_TOOLS, { type }), "canPrint");
  const isPrintEnabled = !ACLStore.can(
    "FeatureFlag:PreventPrintAndScreenshots"
  );

  if (
    (mode === "view" && !canPrint) ||
    (mode === "view" && !showToolPrintOption) ||
    !isPrintEnabled
  )
    return null;

  const onClickPrintTool = e => {
    e.stopPropagation();
    const assessmentToolKey = _.get(_.find(ASSESSMENT_TOOLS, { type }), "key");

    const printType = _.get(_.find(ASSESSMENT_TOOLS, { type }), "printType");
    const printId =
      assessmentToolKey === "mypObjectiveRubric"
        ? resourceId || parentId
        : assessmentToolId;

    getPrintFile({ id: printId || assessmentId, type: printType });
  };

  return (
    <IconButton
      type="plain"
      variant="neutral"
      icon={<PrintOutlined />}
      onClick={onClickPrintTool}
      dsVersion="2.0"
    />
  );
};

const getMediaCardOptions = params => {
  const {
    t,
    mode,
    type,
    assessmentId,
    assessmentTools,
    updateScoreToolData,
    writeScoreToolDataInCache,
    writeAssessmentScoreToolDataInCache,
    parentId,
    parentType,
    textInputRef,
    showWarningIcon,
    scoreToolId,
    sourceFrom,
    isCustomWeights,
    taskWeight,
    setTaskWeight,
    debouncedUpdateAssessment,
  } = params;

  const onClickDropdownMenu = e => {
    e.domEvent.stopPropagation();
    const action = e.key;
    onCardActionClick({ ...params, action });
  };

  const options = getOptions({ t, type, assessmentTools });
  const overlay = (
    <DropdownMenu onClick={onClickDropdownMenu} options={options} />
  );

  const renderIconButton = () => {
    const option = _.first(options);
    return (
      <IconButton
        variant={!option?.isDestructive && "plain"}
        icon={option?.icon}
        onClick={e => {
          e.stopPropagation();
          onCardActionClick({
            ...params,
            action: option?.key,
          });
        }}
      />
    );
  };

  const dropdown = (
    <Dropdown
      getPopupContainer={el => el}
      overlay={overlay}
      placement={"bottomRight"}
    >
      <IconButton
        onClick={e => e.stopPropagation()}
        icon={<DotsVerticalOutlined variant={"subtle"} size={"xx-small"} />}
        variant={"plain"}
      />
    </Dropdown>
  );

  if (_.size(options) === 1) {
    const scoreTool = _.find(assessmentTools, ({ node }) => {
      return _.get(node, "assessmentToolType") === "SCORE";
    });

    const scoreToolData = _.get(
      scoreTool,
      "node.assessmentToolItem.scoreToolData",
      {}
    );

    const maxScore = _.get(scoreToolData, "maxScore", null);

    const onChangeScore = async e => {
      const maxScore = _.get(e, "target.value");
      const maxScoreInt = parseInt(maxScore);

      // allow only digits
      const reg = /^\d+$/;

      if ((reg.test(maxScore) && maxScoreInt <= 1000) || !maxScore) {
        // write score tool data in assessmentTool fragment

        const promises = [];
        if (
          _.includes(
            ["CLASSROOM", "CLASS_PROGRESS_SUMMARY", "GRADEBOOK"],
            sourceFrom
          )
        ) {
          // write max score value in assessmentFragment.assessmentDetails fragment
          // because it is being used in AddAssignment Component
          // there we are reading assessment details from cache
          // we do not allow user to move to next step if max score is empty

          promises.push(
            writeAssessmentScoreToolDataInCache({
              id: scoreToolId,
              maxScore,
              parentId,
              parentType,
              assessmentId,
            })
          );
        }

        await Promise.all([
          writeScoreToolDataInCache({
            id: scoreToolId,
            maxScore,
            parentId,
            parentType,
            assessmentId,
          }),
          // call mutation to save max score
          updateScoreToolData({
            id: scoreToolId,
            maxScore,
            parentId,
            parentType,
            assessmentId,
          }),
          ...promises,
        ]);
      }
    };

    const updateTaskWeight = e => {
      const weight = _.get(e, "target.value");
      const weightFloat = parseFloat(weight);

      // allow only numbers and upto 2 decimals
      if (!DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX.test(weight) && weight) return;

      setTaskWeight(weight);

      debouncedUpdateAssessment?.({
        hasFields: false,
        weightage: weight ? weightFloat : null,
      });
    };

    const onBlurTaskWeight = () => {
      if (_.includes(taskWeight, ".")) {
        setTaskWeight(_.toString(parseFloat(taskWeight)));
      }
    };

    return (
      <div className={classes.mediaCardRightContainer}>
        {type === "SCORE" ? (
          <div className={classes.scoreContainer}>
            <TextInput
              value={maxScore}
              onChange={onChangeScore}
              placeholder={t("common:eg_100")}
              style={styles.scoreInput}
              disabled={mode === "view"}
              ref={textInputRef}
            />
            {isCustomWeights ? (
              <>
                <div className={classes.scoreDivider} />
                <div className={classes.taskWeightContainer}>
                  <div className={classes.taskWeightIconCon}>
                    <WeightOutlined size={"xx-small"} />
                  </div>
                  <div className="text-body-2">{t("common:weight")}</div>
                  <Tooltip
                    tooltip={t("academicSetup:task_weight_tooltip")}
                    overlayInnerStyle={styles.tooltipStyle}
                    placement={"bottom"}
                  >
                    <InformationFilled
                      size={"xx-small"}
                      variant={"subtle"}
                      style={styles.weightInfoIconStyle}
                    />
                  </Tooltip>
                  <TextInput
                    value={taskWeight ? taskWeight : ""}
                    onChange={updateTaskWeight}
                    onBlur={onBlurTaskWeight}
                    placeholder={t("common:eg_100")}
                    style={styles.scoreInput}
                    disabled={mode === "view"}
                  />
                </div>
              </>
            ) : null}
          </div>
        ) : null}
        {type === "DP_ASSESSMENT_RUBRIC" ? renderIconButton() : dropdown}
      </div>
    );
  }

  return (
    <div className={classes.dropdownContainer}>
      {showWarningIcon ? (
        <Tooltip
          placement={"bottom"}
          tooltip={t("classRoom:assessment_tool_eval_warning")}
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <div className={classes.iconContainer}>
            <WarningTriangleOutlined size={"xx-small"} variant={"warning"} />
          </div>
        </Tooltip>
      ) : null}
      {dropdown}
    </div>
  );
};

const getShowEvaluationToggle = params => {
  const {
    assessmentTools,
    mode,
    isDPCriteria,
    curriculumProgramType,
    parentType,
  } = params;

  const showEvaluationDropdown = mode !== "view" && !isDPCriteria;
  if (parentType == "QUESTION") {
    return false;
  }

  if (curriculumProgramType === CURRICULUM_TYPE_DP) {
    const filteredAssessmentTools = _.filter(assessmentTools, ({ node }) => {
      const assessmentToolType = _.get(node, "assessmentToolType");
      return !_.includes(["COMMENT", "SCORE"], assessmentToolType);
    });

    return showEvaluationDropdown && !_.isEmpty(filteredAssessmentTools);
  }

  return showEvaluationDropdown;
};

const getShowMediaCard = params => {
  const {
    assessmentTools,
    showAssessmentToolModal,
    curriculumProgramType,
  } = params;

  const showMediaCard = !_.isEmpty(assessmentTools) && !showAssessmentToolModal;

  if (curriculumProgramType === CURRICULUM_TYPE_DP) {
    const filteredAssessmentTools = _.filter(assessmentTools, ({ node }) => {
      const assessmentToolType = _.get(node, "assessmentToolType");
      return !_.includes(["COMMENT", "SCORE"], assessmentToolType);
    });

    return showMediaCard && !_.isEmpty(filteredAssessmentTools);
  }

  return showMediaCard;
};

const CustomMediaCard = props => {
  const {
    t,
    mode,
    assessmentId,
    assessmentTools = [],
    showAssessmentToolModal,
    updateAssessmentToolData,
    updateAssessmentToolSetting,
    updateInputField,
    updateAssessmentInRedux,
    isDPCriteria = false,
    setMaxScoreValue,
    curriculumProgramType,
    sourceFrom,
    debouncedUpdateAssessment,
    weightage,
    isInstructionsRevamp,
    ignoredAssessmentTools,
    customMediaCardContainerStyle = {},
    parentType,
    academicYearId,
    isUsingNewGradingSetupInCourses,
  } = props;

  const textInputRef = React.useRef(null);
  // this will make sure that max score text input is focused only when it is created
  const isAlreadyFocused = React.useRef(null);

  const value = _.toString(weightage);

  const [taskWeight, setTaskWeight] = useState("");

  const { isScoreEnabled, maxScore, scoreToolId } = getScoreDetailsMemoize({
    assessmentTools: {
      edges: assessmentTools,
    },
  });

  let { isCustomWeights = false } = props;

  const { data: gradingConfigurations } = useFetchQuery({
    query: getAssessmentGradingConfigurationsQuery,
    skipCondition: _.isEmpty(assessmentId) || !isUsingNewGradingSetupInCourses,
    variables: {
      id: assessmentId,
    },
    parseData: data => _.get(data, "node.gradingConfigurations", []),
  });

  if (isUsingNewGradingSetupInCourses) {
    isCustomWeights = getIsCustomWeightsV2(gradingConfigurations);
  }

  useEffect(() => {
    if (isScoreEnabled) {
      setMaxScoreValue(maxScore);
      if (_.isEmpty(_.toString(maxScore)) && !isAlreadyFocused.current) {
        textInputRef.current?.focus();
        isAlreadyFocused.current = true;
      }
    }
  }, [isScoreEnabled]);

  useEffect(() => {
    if (!_.isEqual(value, taskWeight)) {
      setTaskWeight(value);
    }
  }, []);

  const toggleAssessmentToolModal = () => {
    updateAssessmentToolData({
      showAssessmentToolModal: !showAssessmentToolModal,
    });
  };

  const onClickAssessmentToolCard = ({ node }) => {
    const { updateAssessmentInRedux } = props;
    const {
      assessmentToolItem: { id: assessmentToolId },
      assessmentToolType,
    } = node;

    if (_.includes(NON_CLICKABLE_TOOLS, assessmentToolType)) return;

    updateAssessmentToolData({
      assessmentToolModalMode: "view_tool",
    });

    updateAssessmentInRedux({
      assessmentToolId,
      assessmentToolType,
    });

    toggleAssessmentToolModal();
  };

  const onChangeEvaluation = params => {
    const {
      toolId,
      toolType,
      isTeacherEvaluationEnable,
      isStudentEvaluationEnable,
      isTeacherEvaluation = false,
    } = params;
    const { parentType, parentId } = props;

    let mutationParams = {
      toolId,
      toolType,
      parentId: assessmentId,
      parentType: "ASSESSMENT",
    };
    if (parentType == "QUESTION") {
      mutationParams = {
        ...mutationParams,
        parentType: "QUESTION",
        parentId: parentId,
        assessmentId: assessmentId,
        academicYearId,
      };
    }

    if (isTeacherEvaluation) {
      mutationParams = {
        ...mutationParams,
        isTeacherEvaluationEnable,
      };
    } else {
      mutationParams = {
        ...mutationParams,
        isStudentEvaluationEnable,
      };
    }
    updateAssessmentToolSetting(mutationParams);
  };

  const showMediaCard = getShowMediaCard({
    assessmentTools,
    showAssessmentToolModal,
    curriculumProgramType,
  });

  const showEvaluationToggle = getShowEvaluationToggle({
    mode,
    isDPCriteria,
    assessmentTools,
    curriculumProgramType,
    parentType,
  });

  return (
    <React.Fragment>
      {showMediaCard && (
        <div
          className={classes.container}
          style={customMediaCardContainerStyle}
        >
          {showEvaluationToggle && (
            <div className={classes.headerContainer}>
              <div className={classes.assessmentLabel}>
                {parentType == "QUESTION"
                  ? null
                  : t("common:le_assessmentTool_label")}
              </div>
              <div className={classes.flexCenter}>
                <Tooltip
                  tooltip={t("common:teacher_evaluation")}
                  placement={"top"}
                >
                  <UserProfileOutlined variant="subtle" />
                </Tooltip>
              </div>
              <div className={classes.flexCenter}>
                <Tooltip
                  tooltip={t("common:student_evaluation")}
                  placement={"top"}
                >
                  <StudentOutlined variant="subtle" />
                </Tooltip>
              </div>
            </div>
          )}
          {_.map(assessmentTools, ({ node }) => {
            const assessmentTool = _.get(node, "assessmentToolItem", {});
            const assessmentToolId = _.get(node, "assessmentToolItem.id", {});
            const assessmentToolType = _.get(node, "assessmentToolType", "");

            if (
              curriculumProgramType === CURRICULUM_TYPE_DP &&
              _.includes(ignoredAssessmentTools, assessmentToolType)
            ) {
              return;
            }

            const isTeacherEvaluationEnabled = _.get(
              node,
              "isTeacherEvaluationEnable",
              false
            );
            const isStudentEvaluationEnabled = _.get(
              node,
              "isStudentEvaluationEnable",
              false
            );

            const { assessmentToolText } = getAssessmentToolText({
              t,
              assessmentToolType,
              assessmentTool,
            });

            const toolHasEval = _.get(
              _.find(
                ASSESSMENT_TOOLS,
                ({ type }) => type === assessmentToolType
              ),
              "hasEvaluation",
              false
            );

            // in view mode do not show exemplar and anecdotal
            const toolHasPreview =
              mode === "edit" ||
              isAssessmentToolPreviewAvailable({ type: assessmentToolType });

            const {
              allowTeacherEval,
              allowStudentEval,
            } = getAllowTeacherAndStudentEval({
              assessmentToolType,
            });

            const {
              type,
              subtext,
              icon = null,
            } = getAssessmentToolTypeForMediaCardWrapper({
              t,
              assessmentTool,
              assessmentToolType,
              maxScore,
              isInstructionsRevamp,
              mode,
            });

            const mediaCardContainerClass = classNames({
              [classes.mediaCardContainer]: true,
              [classes.multiMediaCardsContainer]:
                mode === "view" || isDPCriteria || !showEvaluationToggle,
            });

            const showWarningIcon =
              !isTeacherEvaluationEnabled && !isStudentEvaluationEnabled;

            if (mode === "view" && showWarningIcon) return;

            const evaluationTexts = getEvaluationTexts({
              t,
              isTeacherEvaluationEnabled,
              isStudentEvaluationEnabled,
              sourceFrom: "CUSTOM_MEDIA_CARD",
              isInstructionsRevamp,
              mode,
              assessmentTool,
              assessmentToolType,
              getEditModeSubtext,
            });

            const editModeSubtext = getEditModeSubtext({
              t,
              assessmentTool,
              assessmentToolType,
            });

            return (
              toolHasPreview && (
                <div className={mediaCardContainerClass} key={assessmentToolId}>
                  {mode === "edit" ? (
                    <MediaCard
                      cardType="medium"
                      type={type}
                      onClick={() => onClickAssessmentToolCard({ node })}
                      name={assessmentToolText}
                      options={getMediaCardOptions({
                        ...props,
                        assessmentToolId,
                        type: assessmentToolType,
                        toggleAssessmentToolModal,
                        updateInputField,
                        updateAssessmentInRedux,
                        textInputRef,
                        showWarningIcon,
                        scoreToolId,
                        maxScore,
                        sourceFrom,
                        isCustomWeights,
                        taskWeight,
                        setTaskWeight,
                        debouncedUpdateAssessment,
                      })}
                      showSubtext={!_.isEmpty(editModeSubtext)}
                      subtext={editModeSubtext}
                      icon={icon}
                    />
                  ) : (
                    <MediaCard
                      cardType="medium"
                      type={type}
                      onClick={() => onClickAssessmentToolCard({ node })}
                      name={isInstructionsRevamp ? subtext : evaluationTexts}
                      subtext={isInstructionsRevamp ? evaluationTexts : subtext}
                      icon={icon}
                      options={getMediaCardViewOptions({
                        ...props,
                        assessmentToolId,
                        type: assessmentToolType,
                      })}
                    />
                  )}

                  {showEvaluationToggle && (
                    <React.Fragment>
                      <div className={classes.flexCenter}>
                        {toolHasEval && allowTeacherEval ? (
                          <ToggleSwitch
                            checked={isTeacherEvaluationEnabled}
                            onChange={e =>
                              onChangeEvaluation({
                                toolId: assessmentToolId,
                                toolType: assessmentToolType,
                                isTeacherEvaluationEnable: e.target.checked,
                                isTeacherEvaluation: true,
                              })
                            }
                            disabled={_.includes(
                              TEACHER_EVALUATION_DISABLED_TOOLS,
                              assessmentToolType
                            )}
                          />
                        ) : (
                          <span>{"-"}</span>
                        )}
                      </div>
                      <div className={classes.flexCenter}>
                        {toolHasEval && allowStudentEval ? (
                          <ToggleSwitch
                            checked={isStudentEvaluationEnabled}
                            onChange={e =>
                              onChangeEvaluation({
                                toolId: assessmentToolId,
                                toolType: assessmentToolType,
                                isStudentEvaluationEnable: e.target.checked,
                              })
                            }
                          />
                        ) : (
                          <Tooltip
                            placement={"top"}
                            tooltip={t(
                              "common:student_eval_not_applicable_for_tool",
                              {
                                label: t("common:lowercase", {
                                  text: assessmentToolText,
                                }),
                              }
                            )}
                          >
                            <span>{"-"}</span>
                          </Tooltip>
                        )}
                      </div>
                    </React.Fragment>
                  )}
                </div>
              )
            );
          })}
        </div>
      )}
      {showAssessmentToolModal && (
        <AssessmentToolV2
          mode={"edit"}
          parentId={props.resourceId}
          {...props}
        />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    assessmentToolData,
    curriculumProgramType,
    mode,
    isInstructionsRevamp,
  } = ownProps;
  const { showAssessmentToolModal } = assessmentToolData;

  const { assessmentId = "" } = state.assessment.assessmentData;

  const courseId = _.get(state, "teacher.selected_class.selected_course");
  const academicYearId = getSelectedAcademicYearId(state);
  const courseGradeFilters = {
    academicYearId,
  };
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });

  const subjectName = _.get(courseDetails, "subjects.0.name", "");

  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    null
  );

  const gradeCalculationMethodSettingName = getGradeCalculationMethodSetting({
    curriculumType: curriculumProgramType,
  });

  const gradeCalculationMethod = getSettingValue({
    name: gradeCalculationMethodSettingName,
    currentCurriculumProgramId: curriculumProgramId,
  });
  const ignoredAssessmentTools = [];
  if (
    isInstructionsRevamp &&
    curriculumProgramType === CURRICULUM_TYPE_DP &&
    mode === "view"
  ) {
    ignoredAssessmentTools.splice(0, ignoredAssessmentTools.length);
  } else {
    ignoredAssessmentTools.push("SCORE", "COMMENT");
  }

  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId,
    academicYearId: academicYearId,
  });

  return {
    assessmentId,
    showAssessmentToolModal,
    subjectName,
    curriculumProgramType,
    isCustomWeights: _.isEqual(gradeCalculationMethod, CUSTOM_WEIGHTS),
    platform: "ADMIN",
    categories: [
      getGradeCalculationMethodCategory({
        curriculumType: curriculumProgramType,
      }),
    ],
    curriculumProgramId,
    gradeCalculationMethodSettingName,
    ignoredAssessmentTools,
    academicYearId,
    isUsingNewGradingSetupInCourses,
  };
};

const mapActionCreators = {
  updateAssessmentInRedux,
  removeAssessmentTool,
  updateAssessmentToolSetting,
  getPrintFile,
  updateScoreToolData,
  writeScoreToolDataInCache,
  writeAssessmentScoreToolDataInCache,
  setMaxScoreValue,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: ["common", "classRoom"] })
)(CustomMediaCard);
